<template>
  <div class="ll-manual">
    <!-- Top bar (desktop / large screens) -->
    <div class="topbar">
      <div>
        <div class="title">Quick Manual Entry</div>
        <div class="hint">
          Tip: paste rows from Excel/Sheets (columns: Item, Qty, Cost, Notes). Cost can be 0; cannot be negative.
        </div>
      </div>

      <div class="actions desktop-only">
        <button class="ll-pill ghost" type="button" @click="$emit('cancel')">Cancel</button>
        <button class="ll-pill" type="button" @click="review">Review</button>
      </div>
    </div>

    <!-- Desktop table -->
    <div class="table-wrap desktop-only">
      <table class="grid" aria-label="Manual entry grid">
        <thead>
          <tr>
            <th style="width: 44%;">Item</th>
            <th style="width: 10%;">Qty</th>
            <th style="width: 16%;">Cost</th>
            <th style="width: 30%;">Notes</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(row, idx) in rows" :key="idx">
            <td>
              <input
                class="cell"
                v-model="row.name"
                placeholder="e.g., Mickey Mantle"
                @keydown.enter.prevent="focusNext(idx, 'qty')"
              />
              <div v-if="rowErrors[idx] && rowErrors[idx].name" class="err">
                {{ rowErrors[idx].name }}
              </div>
            </td>

            <td>
              <input
                class="cell"
                type="number"
                min="1"
                v-model.number="row.quantity"
                @keydown.enter.prevent="focusNext(idx, 'cost')"
              />
              <div v-if="rowErrors[idx] && rowErrors[idx].quantity" class="err">
                {{ rowErrors[idx].quantity }}
              </div>
            </td>

            <td>
              <input
                class="cell"
                type="number"
                step="0.01"
                v-model.number="row.cost"
                @keydown.enter.prevent="focusNext(idx, 'notes')"
              />
              <div v-if="rowErrors[idx] && rowErrors[idx].cost" class="err">
                {{ rowErrors[idx].cost }}
              </div>
            </td>

            <td>
              <input
                class="cell"
                v-model="row.notes"
                placeholder="optional"
                @keydown.enter.prevent="focusNext(idx + 1, 'name')"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <button class="add-row" type="button" @click="addRow">+ Add row</button>
    </div>

    <!-- Mobile cards -->
    <div class="mobile-only">
      <div class="mobile-list">
        <div v-for="(row, idx) in rows" :key="idx" class="row-card">
          <div class="row-head">
            <div class="row-label">Line {{ idx + 1 }}</div>
            <button class="mini" type="button" @click="removeRow(idx)" v-if="rows.length > 1">
              Remove
            </button>
          </div>

          <label class="field">
            <div class="field-label">Item</div>
            <input
              class="cell"
              v-model="row.name"
              placeholder="e.g., Makita drill"
              inputmode="text"
            />
            <div v-if="rowErrors[idx] && rowErrors[idx].name" class="err">
              {{ rowErrors[idx].name }}
            </div>
          </label>

          <div class="two-col">
            <label class="field">
              <div class="field-label">Qty</div>
              <input
                class="cell"
                type="number"
                min="1"
                v-model.number="row.quantity"
                inputmode="numeric"
              />
              <div v-if="rowErrors[idx] && rowErrors[idx].quantity" class="err">
                {{ rowErrors[idx].quantity }}
              </div>
            </label>

            <label class="field">
              <div class="field-label">Cost</div>
              <input
                class="cell"
                type="number"
                step="0.01"
                v-model.number="row.cost"
                inputmode="decimal"
              />
              <div v-if="rowErrors[idx] && rowErrors[idx].cost" class="err">
                {{ rowErrors[idx].cost }}
              </div>
            </label>
          </div>

          <label class="field">
            <div class="field-label">Notes</div>
            <input class="cell" v-model="row.notes" placeholder="optional" />
          </label>
        </div>

        <button class="add-row" type="button" @click="addRow">+ Add row</button>
      </div>

      <!-- Sticky mobile actions -->
      <div class="sticky-actions">
        <button class="ll-pill ghost" type="button" @click="$emit('cancel')">Cancel</button>
        <button class="ll-pill ghost" type="button" @click="addRow">Add line</button>
        <button class="ll-pill" type="button" @click="review">Review</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ManualEntryGrid",
  data() {
    return {
      rows: [
        { name: "", quantity: 1, cost: null, notes: "" },
        { name: "", quantity: 1, cost: null, notes: "" },
        { name: "", quantity: 1, cost: null, notes: "" },
      ],
      rowErrors: {},
    };
  },
  methods: {
    addRow() {
      this.rows.push({ name: "", quantity: 1, cost: null, notes: "" });
    },

    removeRow(i) {
      this.rows.splice(i, 1);
      // re-validate to keep errors aligned
      this.validateRows();
    },

    validateRows() {
      const errors = {};
      this.rows.forEach((r, i) => {
        const rowErr = {};
        const hasAnything =
          (r.name && r.name.trim().length > 0) ||
          (r.notes && r.notes.trim().length > 0) ||
          (r.quantity && r.quantity !== 1) ||
          r.cost !== null;

        if (!hasAnything) return;

        if (!r.name || !r.name.trim()) rowErr.name = "Item name is required.";
        if (r.quantity == null || r.quantity < 1) rowErr.quantity = "Qty must be 1 or more.";
        if (r.cost != null && Number(r.cost) < 0) rowErr.cost = "Cost cannot be negative.";

        if (Object.keys(rowErr).length) errors[i] = rowErr;
      });

      this.rowErrors = errors;
      return Object.keys(errors).length === 0;
    },

    review() {
      if (!this.validateRows()) return;

      const cleanRows = this.rows
        .filter(r => (r.name && r.name.trim()) || r.cost != null || (r.notes && r.notes.trim()))
        .map(r => ({
          name: (r.name || "").trim(),
          quantity: r.quantity || 1,
          cost: r.cost === "" ? null : r.cost,
          notes: (r.notes || "").trim(),
        }));

      const items = cleanRows.map(r => {
        const hasName = !!r.name;
        const hasCost = r.cost !== null && r.cost !== undefined && r.cost !== "";
        const confidence = hasName ? (hasCost ? "high" : "medium") : "low";

        return {
          name: r.name || "",
          quantity: r.quantity,
          cost: hasCost ? Number(r.cost) : null,
          cost_source: hasCost ? "user" : "unknown",
          confidence,
          notes: r.notes || "",
        };
      });

      const flags = [];
      const missingCosts = items.filter(it => it.name && it.cost === null).length;
      const missingNames = items.filter(it => !it.name).length;

      if (missingCosts > 0) flags.push({ severity: "warning", target: "items", message: `${missingCosts} item(s) are missing costs.` });
      if (missingNames > 0) flags.push({ severity: "warning", target: "items", message: `${missingNames} item(s) need a name.` });

      const totalCost = items
        .filter(it => it.cost !== null)
        .reduce((sum, it) => sum + Number(it.cost), 0);

      const draft = {
        acquisition_summary: {
          source: "Manual entry",
          date: new Date().toISOString().slice(0, 10),
          entry_method: "manual",
          total_cost: items.some(it => it.cost !== null) ? Number(totalCost.toFixed(2)) : null,
          cost_strategy: "explicit",
          confidence_level: missingNames > 0 ? "medium" : "high",
        },
        items,
        flags,
        suggestions: [],
      };

      this.$emit("review", draft);
    },

    focusNext(rowIndex, field) {
      this.$nextTick(() => {
        const inputs = this.$el.querySelectorAll("input.cell");
        const perRow = 4;
        const fieldIndex = { name: 0, qty: 1, cost: 2, notes: 3 }[field] ?? 0;

        const idx = Math.max(0, rowIndex) * perRow + fieldIndex;
        if (inputs[idx]) inputs[idx].focus();
        else if (rowIndex >= this.rows.length) {
          this.addRow();
          this.$nextTick(() => {
            const inputs2 = this.$el.querySelectorAll("input.cell");
            const idx2 = (this.rows.length - 1) * perRow + 0;
            if (inputs2[idx2]) inputs2[idx2].focus();
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.ll-manual {
  display: grid;
  gap: 12px;
}

/* Top bar */
.topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.title {
  font-weight: 900;
  font-size: 16px;
}

.hint {
  margin-top: 4px;
  opacity: 0.78;
  font-weight: 650;
  font-size: 12.5px;
  line-height: 1.25rem;
}

.actions {
  display: flex;
  gap: 10px;
}

/* Table */
.table-wrap {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.06);
}

.grid {
  width: 100%;
  border-collapse: collapse;
}

.grid th,
.grid td {
  border-bottom: 1px solid #e6eef3;
  padding: 10px;
  text-align: left;
  vertical-align: top;
}

.grid th {
  background: rgba(230, 238, 243, 0.9);
  font-weight: 900;
  font-size: 12.5px;
}

.cell {
  width: 100%;
  padding: 10px 10px;
  border-radius: 12px;
  border: 1px solid rgba(176, 196, 222, 0.9);
  background: #f6fbff;
  font-weight: 650;
  outline: none;
}

.cell:focus {
  border-color: rgba(50, 123, 168, 0.55);
}

.err {
  margin-top: 6px;
  color: #9f3207;
  font-weight: 800;
  font-size: 12px;
}

/* Add row button */
.add-row {
  width: 100%;
  border: 1px dashed rgba(131, 167, 203, 0.95);
  background: transparent;
  color: #2a3a4a;
  font-weight: 900;
  padding: 12px;
  cursor: pointer;
}

.add-row:hover {
  background: rgba(230, 238, 243, 0.8);
}

.desktop-only {
  display: block;
}
.mobile-only {
  display: none;
}

/* Mobile cards */
.mobile-list {
  display: grid;
  gap: 10px;
}

.row-card {
  border-radius: 16px;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.06);
  padding: 12px;
  display: grid;
  gap: 10px;
}

.row-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.row-label {
  font-weight: 900;
  font-size: 13px;
}

.mini {
  border-radius: 999px;
  border: 1px solid rgba(176, 196, 222, 0.9);
  background: #f6fbff;
  font-weight: 900;
  padding: 6px 10px;
  cursor: pointer;
}

.mini:hover {
  background: rgba(230, 238, 243, 0.8);
}

.field-label {
  font-weight: 900;
  font-size: 12px;
  opacity: 0.85;
  margin-bottom: 6px;
}

.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

/* Sticky actions for mobile */
.sticky-actions {
  position: sticky;
  bottom: 0;
  margin-top: 10px;
  padding: 10px;
  border-radius: 16px;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.10);
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
}

/* Make pills fit nicely */
:deep(.ll-pill) {
  width: 100%;
}

/* Responsive switch */
@media (max-width: 980px) {
  .desktop-only {
    display: none;
  }
  .mobile-only {
    display: block;
  }
  .two-col {
    grid-template-columns: 1fr;
  }
}
</style>
